import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:8009';

/**
 * Eén Socket.io-verbinding, gedeeld door elke consument.
 *
 * Elke hook die live wilde blijven opende tot 22-08-2026 zijn eigen socket. Met de
 * meldingsbel en het statusscherm tegelijk op het scherm stonden er twee verbindingen open
 * voor dezelfde stroom events; elke nieuwe consument had er een derde bij gezet.
 *
 * De socket leeft per token buiten React, met een refcount: de laatste consument die
 * afhaakt sluit hem. Het sluiten wacht een tick, zodat een remount (StrictMode, een
 * tab-wissel) niet steeds op- en afbouwt.
 *
 * Herverbinden is onbeperkt: met een vast aantal pogingen gaf de socket het na een tijd in de
 * achtergrond van de telefoon op en kwam er tot een herstart niets meer binnen. Terug op de
 * voorgrond verbindt hij meteen, zonder de backoff af te wachten.
 */
const verbindingen = new Map();

function pak(token) {
    let entry = verbindingen.get(token);

    if (!entry) {
        const socket = io(WS_URL, {
            auth: { token },
            reconnection: true,
            reconnectionDelay: 1000,
        });
        const opZichtbaar = () => {
            if (document.visibilityState === 'visible' && !socket.connected) socket.connect();
        };
        document.addEventListener('visibilitychange', opZichtbaar);

        entry = { socket, opZichtbaar, refs: 0, sluiter: null };
        verbindingen.set(token, entry);
    }

    if (entry.sluiter) {
        clearTimeout(entry.sluiter);
        entry.sluiter = null;
    }

    entry.refs++;

    return entry;
}

function laatLos(token) {
    const entry = verbindingen.get(token);

    if (!entry) return;

    entry.refs--;

    if (entry.refs > 0) return;

    entry.sluiter = setTimeout(() => {
        if (entry.refs === 0) {
            document.removeEventListener('visibilitychange', entry.opZichtbaar);
            entry.socket.close();
            verbindingen.delete(token);
        }
    }, 0);
}

/** Geeft de gedeelde socket voor dit token, of null zolang er geen token is. */
export function useSharedSocket(token) {
    const [socket, setSocket] = useState(null);

    useEffect(() => {
        if (!token) {
            setSocket(null);

            return undefined;
        }

        setSocket(pak(token).socket);

        return () => laatLos(token);
    }, [token]);

    return socket;
}

export default useSharedSocket;
